import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react'
import type { User } from 'firebase/auth'
import type { Clinic, CorrectionOverride, CorrectionSelection, EligibilityResultWithMembers, EligibilityCheckDoc, EligibilityError, Provider, ConfirmedMember, PlanMember, OrgUser, UserPickOverride, VspBenefits, VspDetailedBenefits, VspSectionsBenefits, EyemedBenefits, EyemedTabsBenefits, VersantBenefits, Invite } from './types'
import { isVersantBenefits } from './types'
import { primaryPaymentOption } from './utils/collectionDisplay'
import { readWorkingClinic, writeWorkingClinic, resolveWorkingClinic, clinicSwitchForRow } from './utils/workingClinic'
import * as Sentry from '@sentry/react'
import { RECENT_CHECKS_PAGE_SIZE, subscribeToRecentChecks, submitCorrection, revertCorrection, getEligibilityCheck, submitUserPick, clearUserPick, type RecentChecksPage } from './services/eligibilityChecks'
import { addClinic, updateClinic, archiveClinic, type ClinicFields } from './services/clinics'
import { logFeature } from './services/activity'
import { logPhiView, setStaffEditSession } from './services/audit'
import { listOrgInvites } from './services/firestore'
import { listOrgUsers } from './services/users'
import { subscribeOrgFlags } from './services/organization'
import NavSidebar from './components/NavSidebar'
import DoctorsList from './components/DoctorsList'
import RecoveryLoopList from './components/RecoveryLoopList'
import type { RecoveryCase } from './services/recoveryCases'
import ClinicsList from './components/ClinicsList'
import UsersList from './components/UsersList'
import EligibilityForm from './components/EligibilityForm'
import type { ManualFormData } from './components/EligibilityForm'
import BenefitsExplorer from './components/BenefitsExplorer'
import MedicalSlideout from './components/MedicalSlideout'
import type { SourceRowHighlight } from './components/MedicalSlideout'
import MemberSelector from './components/MemberSelector'
import RecentChecksTable from './components/RecentChecksTable'
import VspSlideout from './components/VspSlideout'
import EyemedSlideout from './components/EyemedSlideout'
import VersantSlideout from './components/VersantSlideout'
import type { VisionCheckSummary } from './components/VisionSlideoutFrame'
import CheckFailureSlideout from './components/CheckFailureSlideout'
import DailyCockpit from './components/DailyCockpit'
import RpaJobDashboard from './components/RpaJobDashboard'
import RoiAnalytics from './components/RoiAnalytics'
import BenefitsCapture from './components/BenefitsCapture'
import { DEFAULT_FRONT_DESK_GOAL, resolveFrontDeskGoal } from '../shared/frontDeskGoal'
import IntegrationsList from './components/IntegrationsList'
import RawJsonModal from './components/RawJsonModal'
import { deidentifyEligibilityExport } from './utils/deidentify'
import { firestoreDateToIso } from './services/appointments'
import { resolveMember, toConfirmedMember } from './utils/resolveMember'
import type { PatientContext } from './utils/resolveMember'
import { classifyEligibilityError } from './utils/classifyError'
import { stripPayerIdSuffix } from './utils/formatters'
import { DEFAULT_TZ, windowStartUtc, unionWindowStartUtc, type WindowOption } from './utils/recentCheckWindow'
import { buildPatientSearchMatcher } from './utils/patientSearch'
import { filterToClinicScope } from './utils/providerScope'
import { decideClose } from './utils/unsavedWork'
import { useBrowserBackSentinel } from './utils/useBrowserBackSentinel'
import posthog from 'posthog-js'
import { liveDataService } from './demo/liveDataService'
import type { DataService } from './demo/types'
import DemoControls from './components/DemoControls'
import ImpersonationBanner from './components/ImpersonationBanner'
import type { ImpersonationInfo } from './components/ImpersonationBanner'
import SyncHealthChip from './components/SyncHealthChip'
import { Toaster, toast } from 'sonner'
import './Dashboard.css'

// Lazy-loaded: this view pulls in chrono-node (~39KB gz) for robust CSV date
// parsing, so code-splitting it keeps that weight out of the main bundle — it
// loads only when a user actually opens the CSV upload flow.
const CsvUploadView = lazy(() => import('./components/CsvUploadView'))

interface DashboardProps {
  user: User
  orgId: string
  role: string
  clinicId: string | null
  /** True only for VitalRev internal staff (the allowlisted operators). Staff
   *  reach this Dashboard solely via an active impersonation session, so this
   *  doubles as "viewing a tenant". The saved insurance rules are reviewed in
   *  the staff console's Payer Mappings board, never from inside a tenant. */
  isStaff?: boolean
  onSignOut: () => void
  /** Permalink deep-link: a saved eligibility check ID from `?check=<id>`.
   *  Resolved once on mount into the medical slideout. Null when absent. */
  initialCheckId?: string | null
  /** Set when a staff operator is viewing/editing this tenant. */
  impersonation?: ImpersonationInfo | null
  /** Refresh the ID token (pick up new claims) and reload — used by the banner
   *  to exit or to flip view↔edit in place. */
  onReloadSession?: () => void
}

// JSON inspector/export shows in non-prod builds (deployed dev/staging
// included). `import.meta.env.DEV` was serve-only, so built bundles hid it.
const jsonExportEnabled = import.meta.env.MODE !== 'production'

export default function Dashboard({ user, orgId, role, clinicId, isStaff = false, onSignOut, initialCheckId, impersonation, onReloadSession }: DashboardProps) {
  const ds: DataService = liveDataService
  const isAdmin = role === 'admin'
  const isManagerOrAdmin = role === 'admin' || role === 'manager'

  // Navigation
  const [view, setView] = useState<'cockpit' | 'eligibility' | 'doctors' | 'recentChecks' | 'recoveryLoop' | 'rpaJobs' | 'analytics' | 'benefitsCapture' | 'clinics' | 'users' | 'integrations'>('cockpit')

  // Demo mode (#118): the analytics nav/view is hidden in the isDemo org since
  // the ROI callable has no demo dataset behind it. Mirrors DemoControls'
  // self-gating read of the org doc — no demo plumbing leaks into props.
  // Org-doc flags, live: `isDemo` (hides Analytics) + `smsRecoveryEnabled` (the
  // staff master switch — hides the Recovery Loop nav/page + all SMS-recovery
  // affordances when off). Absent → off. A live subscription so a staff toggle
  // reflects without a reload.
  const [demoEnabled, setDemoEnabled] = useState(false)
  const [smsRecoveryEnabled, setSmsRecoveryEnabled] = useState(false)
  // Benefits Capture (staff-set, org-wide): the page switch shows or hides the
  // nav item for EVERY role, and the front-desk goal draws the page's target
  // line — the default when unset or corrupt, resolved in one place.
  const [benefitsCaptureEnabled, setBenefitsCaptureEnabled] = useState(false)
  const [frontDeskGoal, setFrontDeskGoal] = useState(DEFAULT_FRONT_DESK_GOAL)
  useEffect(() => {
    return subscribeOrgFlags(
      orgId,
      flags => {
        setDemoEnabled(flags.isDemo)
        setSmsRecoveryEnabled(flags.smsRecoveryEnabled)
        setBenefitsCaptureEnabled(flags.benefitsCaptureEnabled)
        setFrontDeskGoal(resolveFrontDeskGoal(flags.frontDeskGoal))
      },
      () => { /* unreadable → treat as non-demo / SMS off / page off */ },
    )
  }, [orgId])

  // If the org's SMS recovery is switched off while the user is on the Recovery
  // Loop page, send them back to the Cockpit (the nav item is hidden too).
  useEffect(() => {
    if (!smsRecoveryEnabled && view === 'recoveryLoop') setView('cockpit')
  }, [smsRecoveryEnabled, view])

  // Same for Benefits Capture: staff switching the page off while a reader is
  // on it lands them on the Cockpit without a reload.
  useEffect(() => {
    if (!benefitsCaptureEnabled && view === 'benefitsCapture') setView('cockpit')
  }, [benefitsCaptureEnabled, view])

  // Firestore-backed entities
  const [providers, setProviders] = useState<Provider[]>([])
  const [dataLoading, setDataLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Shared state between form and results
  const [selectedProviderId, setSelectedProviderId] = useState('')

  // Eligibility check state
  const [checking, setChecking] = useState(false)
  const [showRawJson, setShowRawJson] = useState(false)
  const [result, setResult] = useState<EligibilityResultWithMembers | null>(null)
  const [error, setError] = useState<EligibilityError | null>(null)
  const [lastFormData, setLastFormData] = useState<ManualFormData | null>(null)
  const [eligibilityView, setEligibilityView] = useState<'form' | 'csvUpload' | 'memberSelect' | 'slideout' | 'explorer' | 'vspSlideout' | 'eyemedSlideout' | 'versantSlideout' | 'failureSlideout'>('form')
  const [confirmedMember, setConfirmedMember] = useState<ConfirmedMember | null>(null)
  const [lastPatientContext, setLastPatientContext] = useState<PatientContext | null>(null)
  const [formResetKey, setFormResetKey] = useState(0)
  const [prefillFormData, setPrefillFormData] = useState<Partial<ManualFormData>>()
  const [sourceRowHighlight, setSourceRowHighlight] = useState<SourceRowHighlight | null>(null)
  const [returnToView, setReturnToView] = useState<'cockpit' | 'eligibility' | 'doctors' | 'recentChecks' | 'recoveryLoop' | 'rpaJobs' | 'analytics' | 'benefitsCapture' | 'clinics' | 'users' | 'integrations' | null>(null)
  /** Patient ID from a RecentChecksTable row — used by MedicalSlideout to persist DOB corrections. */
  const [slideoutPatientId, setSlideoutPatientId] = useState<string | undefined>(undefined)
  /** AI collection recommendation from the eligibility check doc. */
  const [collectionResult, setCollectionResult] = useState<EligibilityCheckDoc['collectionResult'] | null>(null)
  /** Pre-V2 collection result for legacy docs — rendered by the slideout's legacy card. */
  const [legacyCollectionResult, setLegacyCollectionResult] = useState<EligibilityCheckDoc['legacyCollectionResult'] | null>(null)
  /** Eligibility check doc ID — needed for persisting corrections. */
  const [eligibilityCheckId, setEligibilityCheckId] = useState<string | undefined>(undefined)
  /** Flow (linear correction chain) of the displayed check — handed to the
   *  slideout so a re-run joins the same chain. Null for legacy docs with no
   *  derivable flow. */
  const [slideoutFlowId, setSlideoutFlowId] = useState<string | null>(null)
  /** Stable Stedi payer id for the displayed check — seeds the slideout's edit
   *  payer so re-runs keep Original-Medicare classification. Null on legacy
   *  docs written before the field was persisted. */
  const [slideoutStediId, setSlideoutStediId] = useState<string | null>(null)
  /** Verification timestamp + clinic timezone of the displayed *stored* check —
   *  drives the slideout's prominent date stamp (the payer-dispute record). Null
   *  for freshly-run/rerun checks, which have no filed timestamp yet. */
  const [slideoutCheckMeta, setSlideoutCheckMeta] = useState<{ checkedAt: string; timezone: string } | null>(null)
  /** The displayed *stored* check's own clinic. An admin's Recent Checks and
   *  Cockpit span the whole org, so the check on screen routinely belongs to a
   *  clinic other than the one they are working in — and the slideout's
   *  "save this mapping" writes a per-clinic payer alias
   *  (`clinics/{clinicId}/payerAliases`). Keyed to the working clinic it would
   *  silently steer a DIFFERENT practice's future payer resolution. Null for a
   *  freshly-run check, which genuinely ran under the working clinic. */
  const [slideoutClinicId, setSlideoutClinicId] = useState<string | null>(null)
  /** Raw payer name as received from the appointment source (e.g. Revolution's
   *  "Medicare"). Set when the slideout is opened from the Cockpit; left
   *  undefined when opened from Recent Checks (no appointment context). Drives
   *  the "Suggested by VitalRev AI" badge in the slideout — shown only when
   *  the displayed payer differs from this raw value. */
  const [originalPayerName, setOriginalPayerName] = useState<string | undefined>(undefined)
  /** Whether BenefitsExplorer is in correction mode. */
  const [correctionMode, setCorrectionMode] = useState(false)
  /** Active correction override (from Firestore or just submitted). */
  const [correctionOverride, setCorrectionOverride] = useState<CorrectionOverride | null>(null)
  /** The payment option the user has selected (from Firestore or just picked). */
  const [userPick, setUserPick] = useState<UserPickOverride | null>(null)
  // VSP slideout state — populated when the user opens a VSP check from
  // Recent Checks. The manual VSP submission flow itself is fire-and-forget:
  // a toast prompts the user to track progress in Recent Checks.
  const [vspBenefits, setVspBenefits] = useState<VspBenefits | VspDetailedBenefits | VspSectionsBenefits | null>(null)
  const [eyemedBenefits, setEyemedBenefits] = useState<EyemedBenefits | EyemedTabsBenefits | null>(null)
  const [versantBenefits, setVersantBenefits] = useState<VersantBenefits | null>(null)
  /** Header/identity context for the open vision slideout — built once from the
   *  check doc so the header is identical from the Cockpit and Recent Checks. */
  const [visionSummary, setVisionSummary] = useState<VisionCheckSummary | null>(null)
  // #314: the vision panels need the check itself to offer in-place correction
  // (the summary is display-shaped and carries no identity to edit).
  const [visionCheckDoc, setVisionCheckDoc] = useState<EligibilityCheckDoc | null>(null)
  // Failure slideout context — populated when a row's check has no displayable
  // data. Both the Daily Cockpit and the Recent Checks page feed into this.
  const [failedCheckCtx, setFailedCheckCtx] = useState<{
    check: EligibilityCheckDoc
    /** Shared header data — built via buildVisionSummary so the failure slideout
     *  renders the same header (patient / provider / payer) as the Stedi and
     *  vision slideouts. The raw appointment payer is threaded in as the badge
     *  source via the summary's requestPayerName. */
    summary: VisionCheckSummary
    prefill: Partial<ManualFormData>
  } | null>(null)
  /** Deep-link into the Recovery Loop page: check ID whose case should auto-open
   *  on the Conversation tab. Set by the check slideouts' "View SMS
   *  conversation" link; consumed by RecoveryLoopList once its cases load. */
  const [recoveryFocusCheckId, setRecoveryFocusCheckId] = useState<string | null>(null)
  /** SMS-recovery status of the stored check displayed in a check slideout
   *  (medical/VSP/EyeMed) — drives its "SMS recovery" section. Carries the
   *  check ID so the vision slideouts (which don't track one otherwise) can
   *  deep-link to the Recovery Loop case. Null for fresh (unstored) runs. */
  const [slideoutRecovery, setSlideoutRecovery] = useState<{ inLoop: boolean; smsEligible: boolean; checkId: string } | null>(null)
  /** The Recovery Loop case the open check slideout was reached FROM (#349).
   *  The drawer's "View check" opens the newest check in the case's chain,
   *  which is usually not the case's own check: its doc carries no
   *  recovery-loop flag (only the original is stamped at outreach) and its id
   *  is not the case's id. Without this the slideouts' "View SMS conversation"
   *  link was hidden or disabled on it, and where it did fire it looked the
   *  case up by the wrong id and landed on the bare list. Cleared with the
   *  slideout. */
  const [recoveryReturn, setRecoveryReturn] = useState<{ caseCheckId: string } | null>(null)
  // Synthetic stand-in for a check whose original provider has been deleted.
  // Takes precedence over providers[0] so the slideout shows the original
  // provider name (or "Background check") instead of a random default.
  const [fallbackProvider, setFallbackProvider] = useState<Provider | null>(null)
  // Recent checks cache — lifted here so data survives tab switches
  const [recentChecks, setRecentChecks] = useState<EligibilityCheckDoc[]>([])
  const [checksLoading, setChecksLoading] = useState(false)
  const [checksLoadingMore, setChecksLoadingMore] = useState(false)
  const [checksError, setChecksError] = useState<string | null>(null)
  const [checksCursor, setChecksCursor] = useState<unknown>(null)
  const [checksHasMore, setChecksHasMore] = useState(false)
  // Value unused since the ?demo=1 one-shot fetch was retired; the setter
  // still participates in invalidateChecks' reset choreography.
  const [, setChecksLoaded] = useState(false)
  /** Server-backed sort direction for checkedAt. Toggling triggers a full page-1 re-fetch. */
  const [checksSort, setChecksSort] = useState<'asc' | 'desc'>('desc')
  /** Client-side free-text search — filters across all visible row text. */
  const [searchQuery, setSearchQuery] = useState('')
  /** Client-side clinic filter. 'all' = no filter; otherwise a clinicId. */
  const [clinicFilter, setClinicFilter] = useState<'all' | string>('all')
  /** Time-window selection. Default 'all' (no time bound); other options apply a server-side checkedAt bound. */
  const [windowOption, setWindowOption] = useState<WindowOption>('all')

  // Clinics. Admins load the full active list at mount (boot data — the
  // working clinic resolves from it); non-admins seed just their own clinic.
  const [clinics, setClinics] = useState<Clinic[]>([])
  const [clinicsLoaded, setClinicsLoaded] = useState(false)
  /** Admin clinics fetch failed — surfaced as a banner with retry. Without a
   *  distinct error state, a failed fetch would leave the resolver reading an
   *  empty list as "this org has no clinics" and every clinic-scoped surface
   *  would show the misleading "Create a clinic first" empty state. */
  const [clinicsError, setClinicsError] = useState<string | null>(null)
  /** Bumped to re-run the admin clinics fetch (retry button; an integrations
   *  save refreshes the list in place through this too). */
  const [clinicsRetry, setClinicsRetry] = useState(0)
  /** Display name of the non-admin user's assigned clinic — shown in the header. */
  const [userClinicName, setUserClinicName] = useState<string | null>(null)

  /**
   * The admin's remembered working clinic (see utils/workingClinic.ts). Seeded
   * from localStorage so the first render already has it, then kept honest
   * against the live active-clinic list by the memo below. Non-admins ignore
   * this entirely — their `clinicId` claim wins.
   */
  const [storedClinicId, setStoredClinicId] = useState<string | null>(
    () => readWorkingClinic(orgId, user.uid),
  )

  /**
   * The single clinic that clinic-scoped actions act on (manual check, CSV
   * upload, Doctors). Null while clinics are still loading and
   * when the org has no active clinic — call sites must handle null rather than
   * assert it, because sending `undefined` to a clinic-scoped callable is how
   * "Clinic is not active." class bugs get reintroduced.
   */
  const effectiveClinicId = useMemo(
    () => resolveWorkingClinic({
      assignedClinicId: clinicId,
      storedClinicId,
      // listClinics returns active clinics only. A non-admin's single-clinic
      // seed (getClinic) does NOT status-filter, but their claim short-circuits
      // in the resolver before this list is consulted — for them `clinics` is
      // display data (names, timezone), never resolution input.
      activeClinics: clinicsLoaded ? clinics : [],
    }),
    [clinicId, storedClinicId, clinics, clinicsLoaded],
  )

  // Persist whatever we actually resolved to, so a fallback (first run, or the
  // remembered clinic got archived) becomes the remembered choice.
  useEffect(() => {
    if (!isAdmin || !effectiveClinicId) return
    if (effectiveClinicId === storedClinicId) return
    writeWorkingClinic(orgId, user.uid, effectiveClinicId)
    setStoredClinicId(effectiveClinicId)
  }, [isAdmin, effectiveClinicId, storedClinicId, orgId, user.uid])

  function handleWorkingClinicChange(next: string) {
    if (next === effectiveClinicId) return
    writeWorkingClinic(orgId, user.uid, next)
    setStoredClinicId(next)
  }

  // Org users + invites (admin-only, lazy-loaded on first tab visit)
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([])
  const [orgInvites, setOrgInvites] = useState<Invite[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [usersError, setUsersError] = useState<string | null>(null)

  // Tracks whether the user has paginated past page 1 in the live recent-checks
  // subscription. When true, snapshot updates must NOT rewind `checksCursor` to
  // page 1's last row — doing so would re-fetch pages already visible in the
  // preserved tail. Reset on subscribe (effect start) and on invalidateChecks.
  const hasLoadedMoreChecksRef = useRef(false)

  // Load providers on mount. Keyed to the mount-stable clinic *claim*, never
  // the resolved working clinic: managers/admins fetch the org-wide directory
  // regardless, and a plain user's claim can't change within a session — so
  // this runs once instead of re-firing (and flashing the full-screen loader)
  // every time the working clinic resolves or switches.
  // `background: true` re-fetches without toggling dataLoading — the whole
  // dashboard body unmounts behind that flag (via bootLoading), which would
  // wipe typed form state; the zero-providers empty state's Refresh button
  // uses this.
  const loadData = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background === true
    if (!background) {
      setDataLoading(true)
      setLoadError(null)
    }
    try {
      // Managers load the full org directory (#98 — they can see and import
      // providers from sibling clinics); plain users stay clinic-scoped.
      const provs = await ds.listProviders(orgId, isManagerOrAdmin ? undefined : clinicId ?? undefined)
      setProviders(provs)
    } catch {
      if (background) {
        toast.error('Failed to refresh. Please try again.')
      } else {
        setLoadError('Failed to load data. Please try refreshing.')
      }
    } finally {
      if (!background) setDataLoading(false)
    }
  }, [orgId, clinicId, ds, isManagerOrAdmin])

  useEffect(() => { loadData() }, [loadData])

  // Selection feeds the eligibility form, so a non-admin's default must
  // come from their own clinic even though the directory is org-wide.
  useEffect(() => {
    const selectable = filterToClinicScope(providers, isAdmin, effectiveClinicId)
    if (selectable.length === 0) return
    setSelectedProviderId(prev => {
      // Keep the current selection only if it still exists in the
      // freshly-scoped list. Otherwise reset to the first provider.
      //
      // Why: `setSelectedProviderId` is also called from
      // `handleOpenFromRecentChecks` / `handleRerunResult` with the
      // provider from a prior check, and this effect re-fires on clinic
      // switch. Without this validation, a stale id can survive across
      // contexts, and <select value={staleId}> renders the first
      // <option> visually while React state still holds the mismatched
      // id. Submitting without explicitly touching the dropdown then
      // sends the stale providerId to the backend — the user sees one
      // provider in the UI but the RPA bot receives another.
      const stillValid = prev && selectable.some(p => p.id === prev)
      return stillValid ? prev : selectable[0].id
    })
  }, [providers, isAdmin, effectiveClinicId])
  // Customer Usage telemetry — session + Cockpit. Backend skips staff impersonation.
  useEffect(() => { logFeature('cockpit') }, [])

  // HIPAA PHI-access trail (#185). Staff edit-mode session flag: the write
  // helpers in src/services/* report each direct Firestore write while it's on.
  useEffect(() => {
    setStaffEditSession(impersonation?.mode === 'edit')
    return () => setStaffEditSession(false)
  }, [impersonation])

  // View beacons — an opened eligibility result (any result surface) and, when
  // the slideout is tied to a stored patient record, that patient. Deduped
  // per-resource per-session inside logPhiView. No clinic is sent: the entry
  // must name the clinic the RECORD belongs to, which the backend reads off the
  // record — an admin viewing another clinic's chart carries their own clinic
  // context, and logging that would name the wrong location.
  useEffect(() => {
    if (!eligibilityCheckId) return
    if (eligibilityView === 'slideout' || eligibilityView === 'explorer' || eligibilityView === 'memberSelect') {
      logPhiView('eligibility_result', eligibilityCheckId)
    }
  }, [eligibilityView, eligibilityCheckId])
  useEffect(() => {
    if (eligibilityView === 'slideout' && slideoutPatientId) {
      logPhiView('patient', slideoutPatientId)
    }
  }, [eligibilityView, slideoutPatientId])
  useEffect(() => {
    if (eligibilityView === 'failureSlideout' && failedCheckCtx?.check.id) {
      logPhiView('eligibility_result', failedCheckCtx.check.id)
    }
  }, [eligibilityView, failedCheckCtx])

  /**
   * Server-side lower bound on `checkedAt` for Recent Checks.
   * - 'all' → null (no filter).
   * - Specific clinic context (non-admin or admin with a single-clinic filter) → that clinic's TZ midnight.
   * - Admin "All Clinics" → earliest across all clinic TZs (safe over-fetch).
   * - Clinic doc not yet loaded → DEFAULT_TZ.
   */
  const windowStart = useMemo<Date | null>(() => {
    if (windowOption === 'all') return null
    const now = new Date()
    if (isAdmin && clinicFilter === 'all') return unionWindowStartUtc(windowOption, clinics, now)
    const targetId = isAdmin && clinicFilter !== 'all'
      ? clinicFilter
      : effectiveClinicId
    const tz = (targetId && clinics.find(c => c.id === targetId)?.timezone) || DEFAULT_TZ
    return windowStartUtc(windowOption, tz, now)
  }, [windowOption, isAdmin, clinicFilter, effectiveClinicId, clinics])

  /** Stable string used in effect deps so we don't re-subscribe on date-reference churn. */
  const windowStartKey = windowStart?.toISOString() ?? 'all'

  /**
   * Effective clinicId for the Firestore query.
   * - Non-admin: their auth-bound clinicId (rules require it anyway).
   * - Admin + specific clinic dropdown choice: that clinic's id, so the
   *   `limit(25)` page is filled from THAT clinic and not silently truncated
   *   to whatever rows from other clinics happen to dominate the top.
   * - Admin + 'all': no server-side narrowing (client filters).
   */
  const serverClinicId = useMemo<string | undefined>(() => {
    if (!isAdmin) return clinicId ?? undefined
    if (clinicFilter === 'all') return undefined
    return clinicFilter
  }, [isAdmin, clinicId, clinicFilter])

  // Live mode: subscribe to the first page so new/updated/deleted rows flow
  // through automatically (pending VSP checks flip to success when the
  // webhook resolves, deletions disappear from the list, etc.). The effect
  // intentionally does NOT depend on checksLoaded — that would tear the
  // subscription down as soon as the first snapshot set it to true.
  useEffect(() => {
    if (view !== 'recentChecks') return
    // Fresh subscribe — any prior pagination state is gone, start clean.
    hasLoadedMoreChecksRef.current = false
    setChecksLoading(true)
    setChecksError(null)
    const unsubscribe = subscribeToRecentChecks(
      orgId,
      (docs) => {
        setRecentChecks(prev => {
          // Snapshot only sees page 1 (limit=RECENT_CHECKS_PAGE_SIZE). If the
          // user has loaded additional pages via handleLoadMoreChecks, preserve
          // them — otherwise every background check landing would wipe them.
          // Dedup by id so rows that moved between top and tail don't double.
          if (prev.length <= docs.length) return docs
          const topIds = new Set(docs.map(d => d.id))
          const tail = prev.filter(d => !topIds.has(d.id))
          return [...docs, ...tail]
        })
        // Cursor management: onSnapshot only sees page 1, so the "has more"
        // flag is heuristic — a full page implies more may exist. Load-more
        // picks up the last row's checkedAt string as the startAfter cursor.
        // Skip the cursor rewind once the user has paginated past page 1,
        // otherwise their next "load more" click would re-fetch the tail.
        if (!hasLoadedMoreChecksRef.current) {
          const fullPage = docs.length >= RECENT_CHECKS_PAGE_SIZE
          setChecksHasMore(fullPage)
          setChecksCursor(fullPage ? (docs[docs.length - 1].checkedAt ?? null) : null)
        }
        setChecksLoaded(true)
        setChecksLoading(false)
      },
      (err) => {
        Sentry.captureException(err)
        setChecksError("Couldn't load recent checks. Refresh to retry.")
        setChecksLoading(false)
      },
      undefined,
      serverClinicId,
      checksSort,
      windowStart,
    )
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on windowStartKey, not the windowStart Date, which is a new object every render
  }, [view, orgId, serverClinicId, checksSort, windowStartKey])

  /** Fetch the next page and append to the existing list, preserving current sort direction. */
  const handleLoadMoreChecks = useCallback(async () => {
    if (!checksCursor || checksLoadingMore) return
    setChecksLoadingMore(true)
    try {
      const { docs, lastDoc } = await ds.listRecentChecks(orgId, RECENT_CHECKS_PAGE_SIZE, checksCursor, serverClinicId, checksSort, windowStart)
      // Dedup against current list — the live subscription may have already
      // pulled a few of these rows into the top page between clicks.
      setRecentChecks(prev => {
        const seen = new Set(prev.map(d => d.id))
        const fresh = docs.filter(d => !seen.has(d.id))
        return [...prev, ...fresh]
      })
      setChecksCursor(lastDoc as RecentChecksPage['lastDoc'])
      setChecksHasMore(lastDoc !== null)
      hasLoadedMoreChecksRef.current = true
    } catch (err) {
      setChecksError(err instanceof Error ? err.message : String(err))
    } finally {
      setChecksLoadingMore(false)
    }
  }, [orgId, checksCursor, checksLoadingMore, checksSort, serverClinicId, ds, windowStart])

  // Fetch the assigned clinic for non-admin users. Used for the header name
  // and — critically — to seed `clinics` so windowStart can read the clinic's
  // timezone instead of falling back to DEFAULT_TZ (would mis-window "Today"
  // for non-EST clinics late in the evening).
  useEffect(() => {
    if (isAdmin) return
    // A clinic-less account has nothing to fetch — settle the load immediately
    // so the resolver lands on null and the gated surfaces render their
    // explicit empty states instead of waiting forever.
    if (!clinicId) { setClinicsLoaded(true); return }
    let cancelled = false
    ds.getClinic(orgId, clinicId)
      .then(clinic => {
        if (cancelled || !clinic) return
        setUserClinicName(clinic.name)
        setClinics([clinic])
      })
      .catch(err => { Sentry.captureException(err) })
      // Always flip the flag — a null/error result still ends the load so
      // gated UIs (cockpit) render their empty state instead of hanging on
      // the loading shimmer forever.
      .finally(() => { if (!cancelled) setClinicsLoaded(true) })
    return () => { cancelled = true }
  }, [orgId, clinicId, isAdmin, ds])

  // Admins: the clinics list is boot data. The working clinic — and with it
  // every clinic-scoped surface — resolves from this list, so it loads at
  // mount rather than lazily per tab, and the dashboard body waits on it (see
  // bootLoading below) so `effectiveClinicId` is final before anything
  // renders. A failure surfaces as a banner with retry rather than leaving
  // the resolver reading an empty list as "this org has no clinics".
  // (A manager's single-clinic seed — RPA-jobs name lookups, timezone — is
  // handled by the non-admin effect above.)
  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    setClinicsError(null)
    ds.listClinics(orgId)
      .then(list => { if (!cancelled) { setClinics(list); setClinicsLoaded(true) } })
      .catch(err => {
        Sentry.captureException(err)
        if (!cancelled) setClinicsError('Failed to load clinics. Clinic-scoped pages need this list.')
      })
    return () => { cancelled = true }
  }, [orgId, isAdmin, ds, clinicsRetry])

  // ── Permalink: open a saved eligibility check from `?check=<id>` ──────────
  // Resolve the deep-link once, after providers + clinics have loaded (the
  // provider lookup and the clinic-timezone date stamp both need them). A direct
  // by-ID fetch (getEligibilityCheck) deliberately bypasses the recent-window
  // query, so a filed link still opens after the check ages out of the default
  // view (#83). Guarded by a ref so it fires exactly once.
  const permalinkConsumed = useRef(false)
  useEffect(() => {
    if (!initialCheckId || permalinkConsumed.current) return
    if (dataLoading || !clinicsLoaded) return // wait for providers + clinics
    permalinkConsumed.current = true
    let cancelled = false
    getEligibilityCheck(orgId, initialCheckId)
      .then(check => {
        if (cancelled) return
        if (!check) { toast.error('That eligibility check could not be found.'); return }
        if (!check.result) {
          // Vision checks carry benefits instead of a 271 — open their slideout.
          if (openVisionCheck(check, { returnTo: view })) {
            setView('eligibility')
            return
          }
          // Pending/failed checks have no filed record to display.
          toast('That eligibility check has no saved result to display.')
          return
        }
        // Resolve the original provider; fall back to any clinic provider; last
        // resort synthesize a stand-in from the check identity (mirrors the
        // Recent Checks / Cockpit row-open paths).
        const rawDoctorName = check.identity?.doctorName?.replace(/\s*\(.*\)$/, '').trim()
        const provider: Provider = providers.find(p => p.id === check.providerId) ?? providers[0] ?? {
          id: check.providerId || 'unknown',
          clinicId: check.clinicId ?? '',
          firstName: rawDoctorName || 'Background check',
          lastName: '', npi: '', phone: '', email: '',
        }
        handleBatchViewResult(
          check.result, provider, check.patientId, check.requestSubscriber,
          check.collectionResult, check.id, check.correction, undefined,
          check.checkedAt, check.clinicId, undefined, undefined,
          check.stediPayerId, check,
        )
        setView('eligibility')
      })
      .catch(() => { if (!cancelled) toast.error('That eligibility check is not available.') })
    return () => { cancelled = true }
    // Runs once when data is ready; handleBatchViewResult is hoisted and stable here.
  }, [initialCheckId, dataLoading, clinicsLoaded, orgId, providers]) // eslint-disable-line react-hooks/exhaustive-deps -- runs once when data is ready; handleBatchViewResult is stable here

  // Fetch org users + invites when admin opens the Users tab (skip if already cached).
  // Both are fetched in parallel; a single error state covers both.
  useEffect(() => {
    if (view !== 'users' || usersLoaded || !isAdmin) return
    let cancelled = false
    setUsersLoading(true)
    setUsersError(null)
    Promise.all([listOrgUsers(), listOrgInvites(orgId)])
      .then(([users, invites]) => {
        if (cancelled) return
        setOrgUsers(users)
        setOrgInvites(invites)
        setUsersLoaded(true)
      })
      .catch(err => {
        if (cancelled) return
        const code = err && typeof err === 'object' && 'code' in err
          ? String((err as { code: string }).code)
          : ''
        const message = code === 'functions/internal'
          ? 'An internal error occurred loading users. Please try again or redeploy the functions.'
          : err instanceof Error ? err.message : 'Failed to load users.'
        setUsersError(message)
        Sentry.captureException(err)
      })
      .finally(() => { if (!cancelled) setUsersLoading(false) })
    return () => { cancelled = true }
  }, [view, usersLoaded, isAdmin, orgId])

  /** Background refresh — keeps existing data visible while fetching. */
  function refreshUsers() {
    Promise.all([listOrgUsers(), listOrgInvites(orgId)])
      .then(([users, invites]) => {
        setOrgUsers(users)
        setOrgInvites(invites)
      })
      .catch(err => { Sentry.captureException(err) })
  }

  /** Mark the checks cache as stale so it refetches on next visit. */
  /**
   * Whether this session may write to the tenant at all — false for a
   * view-mode staff impersonation.
   *
   * Computed once and passed to every surface that offers an edit. It used to
   * be inlined for one admin page alone, so the three check editors
   * rendered live fields and a live Save for a read-only session; `requireWrite`
   * in the callable refused them, but only after someone had typed into PHI
   * fields.
   */
  const canWriteTenant = !impersonation || impersonation.mode === 'edit'

  function invalidateChecks() {
    hasLoadedMoreChecksRef.current = false
    setChecksLoaded(false)
    setRecentChecks([])
    setChecksCursor(null)
    setChecksHasMore(false)
  }

  // VSP manual flow: after submit we show a toast and let the user navigate
  // to Recent Checks (which has a live subscription) — no per-check listener.

  /**
   * Toggle the server-side sort direction for the recent checks table.
   * Explicitly resets all pagination state so the load effect fires a fresh page-1 fetch.
   * search/clinicFilter are preserved — they operate on the client-side layer above the fetched data.
   */
  function handleSortChange() {
    setChecksSort(prev => prev === 'desc' ? 'asc' : 'desc')
    setRecentChecks([])
    setChecksCursor(null)
    setChecksHasMore(false)
    setChecksLoaded(false)
  }

  function resetChecksPagination() {
    setRecentChecks([])
    setChecksCursor(null)
    setChecksHasMore(false)
    setChecksLoaded(false)
    hasLoadedMoreChecksRef.current = false
  }

  function handleWindowOptionChange(next: WindowOption) {
    if (next === windowOption) return
    setWindowOption(next)
    resetChecksPagination()
  }

  function handleClinicFilterChange(next: 'all' | string) {
    if (next === clinicFilter) return
    setClinicFilter(next)
    resetChecksPagination()
  }

  const selectedProvider = providers.find(p => p.id === selectedProviderId) ?? fallbackProvider ?? providers[0]

  // ── Recent checks derived data ────────────────────────────────────────────

  /** O(1) clinic name lookups — rebuilt only when the clinics list changes. */
  const clinicsById = useMemo(() => new Map(clinics.map(c => [c.id, c])), [clinics])

  /** O(1) provider lookups — rebuilt only when the providers list changes. */
  const providersById = useMemo(() => new Map(providers.map(p => [p.id, p])), [providers])

  /**
   * Providers selectable for eligibility work in the current clinic context.
   * `providers` holds the org-wide directory for managers (#98), but check
   * submission surfaces (manual form, CSV upload) must only offer doctors
   * assigned to the clinic the check runs at. Admin keeps the full list —
   * matching pre-#98 behavior where admins loaded every provider.
   */
  const clinicProviders = useMemo(
    () => filterToClinicScope(providers, isAdmin, effectiveClinicId),
    [providers, isAdmin, effectiveClinicId],
  )

  /**
   * Derived list of checks after applying clinicFilter then searchQuery.
   * Free-text matching (tokenized any-order name, DOB in common formats,
   * separator-blind member ID, payer/provider/clinic/date) lives in
   * utils/patientSearch.ts, shared with the Daily Cockpit. Field fallbacks
   * mirror what RecentChecksTable renders, so search finds exactly what the
   * row shows.
   */
  const filteredChecks = useMemo(() => {
    // Step 1 — clinic filter (client-side, against already-fetched records)
    const clinicFiltered = clinicFilter === 'all'
      ? recentChecks
      : recentChecks.filter(c => c.clinicId === clinicFilter)

    // Step 2 — free-text search across all visible fields
    const matches = buildPatientSearchMatcher(searchQuery)
    if (!matches) return clinicFiltered

    return clinicFiltered.filter(check => {
      const req = check.requestSubscriber
      const sub = check.result?.subscriber
      const provider = providersById.get(check.providerId)
      const clinic = check.clinicId ? clinicsById.get(check.clinicId) : undefined
      return matches({
        firstName: req?.firstName || sub?.firstName || check.identity?.firstName,
        lastName: req?.lastName || sub?.lastName || check.identity?.lastName,
        dob: req?.dateOfBirth || sub?.dateOfBirth || check.identity?.dob,
        memberId: req?.memberId || sub?.memberId,
        text: [
          check.result?.payer?.name ?? check.payerName,
          provider ? `${provider.lastName} ${provider.firstName}` : check.identity?.doctorName,
          clinic?.name,
          check.checkedAt,
        ],
      })
    })
  }, [recentChecks, clinicFilter, searchQuery, clinicsById, providersById])

  /** Show the clinic filter dropdown only when the user has access to more than one clinic. */
  const showClinicFilter = clinics.length > 1

  // ── Eligibility check handlers ────────────────────────────────────────────

  async function handleCheck(manualForm: ManualFormData) {
    // The eligibility view only renders with a resolved working clinic, so
    // this is unreachable in practice — but never hand a null clinicId to a
    // clinic-scoped callable (the effectiveClinicId contract above).
    if (!effectiveClinicId) return
    setChecking(true)
    setError(null)
    setResult(null)
    setConfirmedMember(null)
    setCollectionResult(null)
    setLegacyCollectionResult(null)
    setEligibilityCheckId(undefined)
    setSlideoutFlowId(null)
    setSlideoutStediId(null)
    setSlideoutCheckMeta(null)
    // This run's slideout belongs to the working clinic. Both of these are set
    // only when a STORED check is opened, and this path reaches the slideout
    // without going through `handleBatchViewResult` (or `handleMemberSelected`,
    // which clears the payer name for the same reason) — so leaving them would
    // hand a fresh check the PREVIOUSLY VIEWED check's clinic, and the
    // slideout's "Map it for this clinic" would write that other practice's
    // payer alias.
    setSlideoutClinicId(null)
    setOriginalPayerName(undefined)
    setCorrectionOverride(null)
    setUserPick(null)
    setCorrectionMode(false)
    setLastFormData(manualForm)

    const patientContext: PatientContext = {
      firstName: manualForm.firstName,
      lastName: manualForm.lastName,
      dateOfBirth: manualForm.dateOfBirth,
      memberId: manualForm.memberId,
    }
    setLastPatientContext(patientContext)

    try {
      // Server routes by payer name: Stedi resolves inline; VSP bot runs
      // async and returns a pending checkId the UI tracks via Recent Checks.
      const response = await ds.verifyEligibility({
        orgId,
        clinicId: effectiveClinicId,
        providerId: selectedProviderId,
        subscriber: {
          firstName: manualForm.firstName,
          lastName: manualForm.lastName,
          memberId: manualForm.memberId,
          dateOfBirth: manualForm.dateOfBirth,
        },
        tradingPartnerServiceId: manualForm.tradingPartnerServiceId,
        stediPayerId: manualForm.stediPayerId,
        payerName: manualForm.payerName,
        // Present when the form was opened to correct an existing check /
        // cockpit row — the new check joins that flow and inherits its patient
        // contact + SMS-recovery candidacy from the source check server-side.
        // A fresh lookup carries neither and never enters the SMS loop.
        flowId: manualForm.flowId,
        sourceCheckId: manualForm.sourceCheckId,
        // Set only when the form was opened from a cockpit row that carries no
        // check yet — the server stamps the resulting check onto it so the row
        // stops reading "Cannot check" once it has been answered.
        appointmentId: manualForm.appointmentId,
      })

      if (response.kind === 'rpa_pending') {
        // Async RPA bot job — notify and let the user drill in from Recent Checks.
        // The vendor ID tells us which bot to name in the toast (VSP, EyeMed, …).
        const vendorLabel = response.rpaVendor.toUpperCase()
        toast.success(
          response.reused
            ? 'This check is already in progress'
            : `${vendorLabel} check started`,
          {
            description: (
              <div className="toast-action-body">
                <span>
                  {response.reused
                    ? 'A prior check for this patient is still running. View status in Recent Checks.'
                    : 'This may take a few minutes. View status in Recent Checks when ready.'}
                </span>
                <button
                  type="button"
                  className="toast-action-body__btn"
                  onClick={() => { invalidateChecks(); setView('recentChecks') }}
                >
                  Go to Recent Checks
                </button>
              </div>
            ),
          },
        )
        invalidateChecks()
        setLastFormData(null)
        // Prefill consumed — clear it so the remounted form can't carry this
        // chain's hidden sourceCheckId onto a different patient typed over it.
        setPrefillFormData(undefined)
        return
      }

      // response.kind === 'stedi_resolved'
      const eligResult = response.data
      setCollectionResult(response.collection ?? null)
      setEligibilityCheckId(response.checkId ?? undefined)
      // A fresh check starts (or continues) its flow — the server echoes the
      // chain key so an immediate correction re-run threads onto it.
      setSlideoutFlowId(response.flowId ?? manualForm.flowId ?? null)
      // Server echo is authoritative; fall back to the form's pick (demo
      // service doesn't echo it).
      setSlideoutStediId(response.stediPayerId ?? manualForm.stediPayerId ?? null)
      setUserPick(null)

      // Backward compat: old Firestore data may lack members array
      const members = eligResult.members ?? [{
        firstName: eligResult.subscriber.firstName,
        lastName: eligResult.subscriber.lastName,
        memberId: eligResult.subscriber.memberId,
        dateOfBirth: eligResult.subscriber.dateOfBirth,
        relationship: 'self' as const,
        relationshipCode: '18',
      }]
      const resultWithMembers = { ...eligResult, members }

      setResult(resultWithMembers)
      setLastFormData(null)
      // Prefill consumed — clear it so the remounted form can't carry this
      // chain's hidden sourceCheckId onto a different patient typed over it.
      setPrefillFormData(undefined)
      invalidateChecks()

      // Sparse response: payer replied but returned no usable member/benefit data
      const isSparse = members.length === 0
        || (members.every(m => !m.firstName && !m.lastName) && eligResult.status === 'unknown')

      if (isSparse) {
        // Use original form input so the patient name still displays
        setConfirmedMember({
          firstName: patientContext.firstName,
          lastName: patientContext.lastName,
          memberId: patientContext.memberId ?? '',
          dateOfBirth: patientContext.dateOfBirth ?? '',
          relationship: 'self',
          isDependent: false,
        })
        setEligibilityView('slideout')
      } else {
        const resolution = resolveMember(members, patientContext)
        if (resolution.type === 'exact') {
          setConfirmedMember(toConfirmedMember(resolution.member))
          setEligibilityView('slideout')
        } else {
          setEligibilityView('memberSelect')
        }
      }
    } catch (err) {
      Sentry.captureException(err)
      // Dedup case: a prior check for this patient is already pending. The
      // server throws `already-exists`. Surface as a toast so the user can
      // jump straight to Recent Checks instead of seeing an inline error.
      const code = err && typeof err === 'object' && 'code' in err
        ? String((err as { code: string }).code)
        : ''
      if (code === 'functions/already-exists') {
        toast.warning('This check is already in progress', {
          description: (
            <div className="toast-action-body">
              <span>A prior check for this patient is still running. View status in Recent Checks.</span>
              <button
                type="button"
                className="toast-action-body__btn"
                onClick={() => { invalidateChecks(); setView('recentChecks') }}
              >
                Go to Recent Checks
              </button>
            </div>
          ),
        })
        setLastFormData(null)
        // Same as the success exits: the submission was consumed (a prior
        // check is already running this flow) — drop the stale prefill.
        setPrefillFormData(undefined)
      } else {
        setError(classifyEligibilityError(err))
      }
    } finally {
      setChecking(false)
      setFormResetKey(k => k + 1)
    }
  }

  function handleRetry() {
    if (checking || !lastFormData) return
    handleCheck(lastFormData)
  }

  // Analytics: fire once per error (null → non-null transition only)
  useEffect(() => {
    if (!error) return
    if (posthog.__loaded) {
      posthog.capture('eligibility_error_shown', {
        category: error.category,
        retryable: error.retryable,
      })
    }
  }, [error])

  function handleNavClick(target: typeof view) {
    if (target === view) return
    setView(target)
    setPrefillFormData(undefined)
    // Reset eligibility sub-view to prevent stale state when switching tabs
    if (eligibilityView !== 'form') {
      setResult(null)
      setError(null)
      setConfirmedMember(null)
      setCollectionResult(null)
      setLegacyCollectionResult(null)
      setEligibilityCheckId(undefined)
      setSlideoutFlowId(null)
      setSlideoutCheckMeta(null)
      setCorrectionOverride(null)
      setUserPick(null)
      setCorrectionMode(false)
      setVspBenefits(null)
      setEyemedBenefits(null)
      setVersantBenefits(null)
      setVisionSummary(null)
      setSlideoutRecovery(null)
      setRecoveryReturn(null)
      setEligibilityView('form')
    }
  }

  function handleProviderChange(id: string) {
    setSelectedProviderId(id)
    setResult(null)
    setError(null)
    setConfirmedMember(null)
    setEligibilityView('form')
  }

  function handleBack() {
    setResult(null)
    setError(null)
    setConfirmedMember(null)
    setVspBenefits(null)
    setEyemedBenefits(null)
    setVersantBenefits(null)
    setCollectionResult(null)
    setLegacyCollectionResult(null)
    setUserPick(null)
    setEligibilityView('form')
    if (returnToView) {
      setView(returnToView)
      setReturnToView(null)
    }
  }

  function handleSlideoutClose() {
    setResult(null)
    setError(null)
    setConfirmedMember(null)
    setCollectionResult(null)
    setLegacyCollectionResult(null)
    setUserPick(null)
    setVspBenefits(null)
    setEyemedBenefits(null)
    setVersantBenefits(null)
    setFailedCheckCtx(null)
    setFallbackProvider(null)
    setSlideoutRecovery(null)
    setRecoveryReturn(null)
    // Reset to a provider from THIS clinic so the form's <select> has a
    // matching option (slideouts can set selectedProviderId to a synthetic
    // stand-in id, or to a sibling clinic's doctor off a stored check).
    // Must read clinicProviders, not the org-wide directory: a manager's
    // `providers` spans every clinic since #98, so seeding from it leaves an
    // id the dropdown can't render — and, at a clinic with no doctors, an
    // id that enables the Check button under the "no doctors assigned" panel.
    setSelectedProviderId(clinicProviders[0]?.id ?? '')
    setEligibilityView('form')
    if (returnToView) {
      setView(returnToView)
      setReturnToView(null)
    }
  }

  /** Check slideout (medical, VSP/EyeMed, or failure) → Recovery Loop: close
   *  the slideout and land on the case's SMS conversation. */
  function handleViewRecoveryConversation(checkId: string) {
    setResult(null)
    setError(null)
    setConfirmedMember(null)
    setCollectionResult(null)
    setLegacyCollectionResult(null)
    setUserPick(null)
    setVspBenefits(null)
    setEyemedBenefits(null)
    setVersantBenefits(null)
    setFailedCheckCtx(null)
    setFallbackProvider(null)
    setSlideoutRecovery(null)
    setRecoveryReturn(null)
    setSelectedProviderId(clinicProviders[0]?.id ?? '')
    setEligibilityView('form')
    setReturnToView(null)
    // A check reached from a case goes back to THAT case, whatever its own id.
    setRecoveryFocusCheckId(recoveryReturn?.caseCheckId ?? checkId)
    setView('recoveryLoop')
  }

  /** Recovery Loop slideout → an eligibility check, opened over the Recent
   *  Checks page (closing the slideout leaves staff on the full checks list
   *  rather than bouncing back to the loop). `checkId` is the drawer's link
   *  target (#279): the fired re-run when one exists, the case's originating
   *  check otherwise — so this must route SUCCESSES (medical + vision) and
   *  still-pending re-runs, not just failures. Fetches the full doc (the case
   *  row carries only a projection) and routes it the same way a Recent Checks
   *  row-open would. */
  async function handleOpenCheckFromRecovery(rc: RecoveryCase, checkId: string = rc.checkId) {
    try {
      const check = await getEligibilityCheck(orgId, checkId)
      if (!check) {
        toast.error('That eligibility check could not be found.')
        return
      }
      setView('recentChecks')
      // Whichever slideout opens below, it was reached from THIS case, and the
      // way back must land on it — see `recoveryReturn`. Set after the routing
      // helpers, which reset the slideout's recovery state from the doc.
      const fromCase = { caseCheckId: rc.checkId }
      if (check.result) {
        // A filed 271 — a resolved medical re-run (#279), or a rare filed
        // original — renders as the medical slideout; mirror the permalink path.
        const rawDoctorName = check.identity?.doctorName?.replace(/\s*\(.*\)$/, '').trim()
        const provider: Provider = providers.find(p => p.id === check.providerId) ?? providers[0] ?? {
          id: check.providerId || 'unknown',
          clinicId: check.clinicId ?? '',
          firstName: rawDoctorName || 'Background check',
          lastName: '', npi: '', phone: '', email: '',
        }
        handleBatchViewResult(
          check.result, provider, check.patientId, check.requestSubscriber,
          check.collectionResult, check.id, check.correction, undefined,
          check.checkedAt, check.clinicId, check.userPick, check.legacyCollectionResult,
          check.stediPayerId, check,
        )
        // handleBatchViewResult captured the pre-navigation `view`
        // ('recoveryLoop') as the return target — override so Close stays on
        // Recent Checks.
        setReturnToView('recentChecks')
        setRecoveryReturn(fromCase)
        return
      }
      // Vision checks carry benefits instead of a 271 — a resolved VSP/EyeMed
      // re-run opens its own slideout (#279), same as the ?check= permalink.
      if (openVisionCheck(check, { returnTo: 'recentChecks' })) {
        setRecoveryReturn(fromCase)
        return
      }
      if (check.status === 'pending') {
        // A fired re-run that hasn't settled: nothing is filed yet, and the
        // failure slideout would caption it "failed". Recent Checks is already
        // open — its row tracks the live status.
        toast('That check is still running — its row in Recent Checks updates live.')
        return
      }
      const firstName = check.requestSubscriber?.firstName ?? check.identity?.firstName ?? ''
      const lastName = check.requestSubscriber?.lastName ?? check.identity?.lastName ?? ''
      const dob = check.requestSubscriber?.dateOfBirth ?? check.identity?.dob ?? ''
      const memberId = check.requestSubscriber?.memberId ?? check.identity?.memberId ?? check.identity?.policyNumber ?? ''
      const payer = check.payerName ?? ''
      setFailedCheckCtx({
        check,
        summary: buildVisionSummary(check),
        prefill: {
          firstName,
          lastName,
          dateOfBirth: dob,
          memberId,
          payerName: stripPayerIdSuffix(payer),
          appointmentDate: check.appointmentDate ?? '',
          flowId: check.flowId ?? undefined,
          sourceCheckId: check.id,
        },
      })
      setReturnToView('recentChecks')
      setEligibilityView('failureSlideout')
      setRecoveryReturn(fromCase)
    } catch {
      toast.error('That eligibility check is not available.')
    }
  }

  function handleViewFullBenefits() {
    setSourceRowHighlight(null)
    setView('eligibility')
    setEligibilityView('explorer')
  }

  // Returning from the full-page Benefits Explorer to the slideout overlay.
  // handleViewFullBenefits switched `view` to 'eligibility' to render the
  // explorer; restore the view the slideout was opened over (the cockpit, when
  // opened from there — tracked in returnToView) so the slideout sits over the
  // patient list, not the eligibility form. Falls through to whatever view the
  // slideout was already over (e.g. a fresh check from the eligibility page).
  function handleBackToSlideout() {
    setEligibilityView('slideout')
    if (returnToView) setView(returnToView)
  }

  // The single definition of "one level up from the explorer" — in correction
  // mode that's dismissing the correction, otherwise plain back-to-slideout.
  // Both the browser-Back sentinel and the explorer's own in-app Back button
  // read it from here so the two can't drift apart.
  function handleExplorerBack() {
    if (correctionMode) handleCorrectionDismiss()
    else handleBackToSlideout()
  }

  // ── Browser Back button ───────────────────────────────────────────
  // No router here, so the hardware/browser Back button would otherwise leave
  // the SPA ("go to nothing") while a patient-detail view is open. Every
  // nested level pushes its own sentinel via the shared stack (#231,
  // src/utils/browserBackStack.ts) — same mechanism used by VspSlideout's
  // plan-table modal — so a normal Back press undoes one level at a time,
  // innermost first (a multi-entry jump via the long-press Back menu unwinds
  // every level it skips; see the stack's module docs for the edges).
  const DETAIL_VIEWS: ReadonlyArray<string> =
    ['memberSelect', 'slideout', 'explorer', 'vspSlideout', 'eyemedSlideout', 'versantSlideout', 'failureSlideout']
  const detailOpen = DETAIL_VIEWS.includes(eligibilityView)
  // 'explorer' is a SECOND level nested inside 'slideout' — reached only via
  // handleViewFullBenefits/handleViewSourceRows/handleStartCorrection, all
  // fired from an already-open slideout.
  const nestedDetailOpen = eligibilityView === 'explorer'

  // #274's third close path. Escape and the overlay were wired inside the
  // panels; browser Back went straight through `goHomeToCockpit` and discarded
  // a half-typed correction with no warning at all. The rule is the same one —
  // warn once, let a second press through — so it uses the same decision
  // function rather than a second opinion about what "unsaved" means.
  const [detailDirty, setDetailDirty] = useState(false)
  const [detailWarned, setDetailWarned] = useState(false)

  const goHomeToCockpit = useCallback(() => {
    setResult(null)
    setError(null)
    setConfirmedMember(null)
    setCollectionResult(null)
    setVspBenefits(null)
    setEyemedBenefits(null)
    setVersantBenefits(null)
    setVisionSummary(null)
    setFailedCheckCtx(null)
    setFallbackProvider(null)
    setSlideoutRecovery(null)
    setRecoveryReturn(null)
    setCorrectionOverride(null)
    setCorrectionMode(false)
    setSelectedProviderId(clinicProviders[0]?.id ?? '')
    setReturnToView(null)
    setEligibilityView('form')
    setView('cockpit')
  }, [clinicProviders])

  // Reset on every view CHANGE, not in each close handler. The flag belongs to
  // whichever panel is mounted — panels render on `eligibilityView`, so any
  // transition means its owner is gone. There are six paths that drop a detail
  // view (the X, Escape, the overlay, Edit-&-retry, a correction landing, a
  // nav-sidebar click) plus sideways moves like slideout → explorer, and a
  // stale `true` left by ANY of them would make the next first Back press do
  // nothing with no panel on screen to explain why — the dead-press complaint
  // #231 exists about. Keying on the view covers a path added later too.
  useEffect(() => {
    setDetailDirty(false)
    setDetailWarned(false)
  }, [eligibilityView])

  /** Browser Back on an open detail view: refuse the first press while there
   *  is typed work, so the panel's own inline warning is what the operator
   *  sees. A second press closes — someone pressing Back twice means it. */
  const backOutOfDetail = useCallback((): void | 'refused' => {
    if (decideClose(detailDirty, detailWarned) === 'confirm') {
      setDetailWarned(true)
      return 'refused'
    }
    goHomeToCockpit()
  }, [detailDirty, detailWarned, goHomeToCockpit])

  // The NESTED sentinel must be registered before the outer one: when both
  // close in one commit (a nav-sidebar click while the Explorer is open resets
  // eligibilityView straight to 'form'), effects run in hook order, and slots
  // only reclaim innermost-first — outer-first would release the outer id
  // while the explorer's still sits above it, stranding one history slot per
  // nav-away (see "closing views innermost-first" in browserBackStack.test.ts).
  // Push order is unaffected: the explorer can only open from an already-open
  // slideout, never in the same commit as it.
  useBrowserBackSentinel(nestedDetailOpen, handleExplorerBack)
  useBrowserBackSentinel(detailOpen, backOutOfDetail)

  function handleViewSourceRows(highlight: SourceRowHighlight) {
    setSourceRowHighlight(highlight)
    setView('eligibility')
    setEligibilityView('explorer')
  }

  function handleMemberSelected(member: PlanMember) {
    setConfirmedMember(toConfirmedMember(member))
    // Manual flow — staff picked the payer themselves, so there's no raw
    // appointment name to compare against. Explicitly clear so a prior
    // Cockpit-opened slideout doesn't leave a stale value behind that would
    // light up the indicator in the wrong place.
    setOriginalPayerName(undefined)
    setEligibilityView('slideout')
  }

  /** Build the vision slideout's header context from a stored check doc so the
   *  header is identical regardless of entry point (Cockpit vs Recent Checks).
   *  Falls back to the appointment's own date / raw payer when the check doc
   *  doesn't carry them (older vision checks). */
  function buildVisionSummary(
    check: EligibilityCheckDoc,
    opts?: { fallbackAppointmentDate?: string; fallbackRawPayer?: string },
  ): VisionCheckSummary {
    const firstName = check.requestSubscriber?.firstName ?? check.identity?.firstName ?? ''
    const lastName = check.requestSubscriber?.lastName ?? check.identity?.lastName ?? ''
    // Resolve the provider: the check's recorded provider — stored on new
    // checks by runRpaPath, and on legacy checks by the backfill:vsp-providers
    // script. No recorded provider → show the stored doctor name (if any) with
    // an em-dash NPI instead of guessing: the old providers[0] fallback showed
    // a random clinic provider (for an org-wide admin, even another clinic's)
    // as if the check were attributed to them. Deliberately diverges from the
    // medical slideout's fallback, where the provider is functional (re-run
    // submission), not display-only.
    const provider = providers.find(p => p.id === check.providerId)
    const doctorName = check.identity?.doctorName?.replace(/\s*\(.*\)$/, '').trim()
    const providerName = (provider ? `${provider.firstName} ${provider.lastName}`.trim() : '') || doctorName || undefined
    return {
      patientName: `${firstName} ${lastName}`.trim() || '—',
      appointmentDate: check.appointmentDate || opts?.fallbackAppointmentDate || undefined,
      dob: check.requestSubscriber?.dateOfBirth ?? check.identity?.dob ?? undefined,
      memberId: check.memberId ?? check.requestSubscriber?.memberId ?? check.identity?.memberId ?? check.identity?.policyNumber ?? undefined,
      payerName: check.payerName ?? check.result?.payer?.name ?? opts?.fallbackRawPayer ?? undefined,
      requestPayerName: check.requestPayerName ?? opts?.fallbackRawPayer ?? undefined,
      providerName,
      providerNpi: provider?.npi || undefined,
      checkedAt: check.checkedAt || undefined,
      timezone: clinicsById.get(check.clinicId ?? '')?.timezone || DEFAULT_TZ,
      checkId: check.id,
    }
  }

  /** Open a vision (VSP/EyeMed) check in its slideout. Returns false when the
   *  check carries no vision benefits, so callers can fall through to other
   *  handling. Shared by the Cockpit, Recent Checks, and the ?check= deep-link
   *  so all three open an identical slideout. */
  function openVisionCheck(
    check: EligibilityCheckDoc,
    opts: { returnTo: typeof view; fallbackAppointmentDate?: string; fallbackRawPayer?: string },
  ): boolean {
    const benefits = check.benefits ?? check.vspBenefits
    if (!benefits) return false
    // HIPAA PHI-access trail (#185): opening a vision result is a PHI view.
    // Logged at this shared entry point so the Cockpit, Recent Checks, and the
    // ?check= deep-link all leave the same trail. (The vision slideout views
    // don't set eligibilityCheckId, so the effect-based beacons above never
    // fire for them.)
    logPhiView('eligibility_result', check.id)
    setVisionSummary(buildVisionSummary(check, opts))
    setVisionCheckDoc(check)
    setSlideoutRecovery({
      inLoop: !!check.inRecoveryLoop,
      // Manual-origin chains carry no SMS candidacy (`smsOutreachAttempted`
      // absent) — never hint that a text will be sent for them.
      smsEligible: check.recoveryAction === 'patient_correction'
        && check.smsOutreachAttempted !== undefined,
      checkId: check.id,
    })
    setReturnToView(opts.returnTo)
    if (check.type === 'versant' && isVersantBenefits(benefits)) {
      setVersantBenefits(benefits)
      setEyemedBenefits(null)
      setVspBenefits(null)
      setEligibilityView('versantSlideout')
    } else if (check.type === 'eyemed') {
      setEyemedBenefits(benefits as EyemedBenefits | EyemedTabsBenefits)
      setVspBenefits(null)
      setEligibilityView('eyemedSlideout')
    } else {
      setVspBenefits(benefits as VspBenefits | VspDetailedBenefits | VspSectionsBenefits)
      setEyemedBenefits(null)
      setEligibilityView('vspSlideout')
    }
    return true
  }

  function handleBatchViewResult(
    batchResult: EligibilityResultWithMembers,
    provider: Provider,
    checkPatientId?: string,
    requestSubscriber?: { firstName: string; lastName: string; memberId: string; dateOfBirth: string },
    checkCollectionResult?: EligibilityCheckDoc['collectionResult'],
    checkId?: string,
    checkCorrection?: CorrectionOverride,
    /** Raw payer name from the appointment source. Set when opened from the
     *  Cockpit (the Cockpit row knows what Revolution actually sent); left
     *  undefined when opened from Recent Checks. Drives the slideout's
     *  "Suggested by VitalRev AI" badge. */
    rawAppointmentPayer?: string,
    /** When the check was verified with the payer (ISO) — powers the slideout's
     *  date stamp. Pass `check.checkedAt` for stored checks; omit for fresh runs. */
    checkCheckedAt?: string,
    /** The check's own clinic ID — used to render `checkCheckedAt` in that
     *  clinic's timezone (an admin may view another clinic's check). */
    checkClinicId?: string,
    checkUserPick?: UserPickOverride,
    checkLegacyCollectionResult?: EligibilityCheckDoc['legacyCollectionResult'],
    /** Stable Stedi payer id stored on the check doc — pass `check.stediPayerId`
     *  so slideout re-runs keep Original-Medicare classification. */
    checkStediPayerId?: string | null,
    /** The stored check doc (callers pass the check itself) — supplies the
     *  recovery-loop fields for the "SMS recovery" section and the flow key a
     *  re-run threads onto. Omit for fresh runs. */
    storedCheck?: Pick<EligibilityCheckDoc, 'inRecoveryLoop' | 'recoveryAction' | 'flowId' | 'smsOutreachAttempted'>,
  ) {
    // Synthetic stand-in only fires when the clinic has no providers at all
    // (call site's `?? providers[0]` catches the deleted-provider / cross-clinic
    // cases before reaching here). Store it as fallback so `selectedProvider`
    // resolves to it instead of undefined.
    const isSynthetic = !providers.some(p => p.id === provider.id)
    setFallbackProvider(isSynthetic ? provider : null)
    setResult(batchResult)
    setSelectedProviderId(provider.id)
    setSlideoutPatientId(checkPatientId)
    setCollectionResult(checkCollectionResult ?? null)
    setLegacyCollectionResult(checkLegacyCollectionResult ?? null)
    setEligibilityCheckId(checkId)
    // Stored docs present a flow key via the read layer's legacy fallback.
    setSlideoutFlowId(storedCheck?.flowId ?? null)
    setSlideoutStediId(checkStediPayerId ?? null)
    setSlideoutCheckMeta(checkCheckedAt
      ? { checkedAt: checkCheckedAt, timezone: clinicsById.get(checkClinicId ?? '')?.timezone || DEFAULT_TZ }
      : null)
    setSlideoutClinicId(checkClinicId ?? null)
    setCorrectionOverride(checkCorrection ?? null)
    setUserPick(checkUserPick ?? null)
    setCorrectionMode(false)
    setOriginalPayerName(rawAppointmentPayer)
    // When the org's SMS recovery is off, suppress every SMS-recovery affordance
    // in the slideouts (the "View SMS conversation" link + the eligible hint).
    // Manual-origin chains carry no SMS candidacy (`smsOutreachAttempted`
    // absent) — the eligible hint would falsely promise a text.
    setSlideoutRecovery(smsRecoveryEnabled && storedCheck && checkId
      ? {
        inLoop: !!storedCheck.inRecoveryLoop,
        smsEligible: storedCheck.recoveryAction === 'patient_correction'
          && storedCheck.smsOutreachAttempted !== undefined,
        checkId,
      }
      : null)
    // Prefer requestSubscriber (what we sent) over 271 response (what payer
    // returned). Some checks carry neither (RPA/demo-seeded docs) — fall back
    // to the result's first plan member, else open with a blank identity
    // rather than crashing the click handler (VITALREV-WEB-13).
    const sub = requestSubscriber ?? batchResult.subscriber ?? batchResult.members?.[0]
    setConfirmedMember({
      firstName: sub?.firstName ?? '',
      lastName: sub?.lastName ?? '',
      memberId: sub?.memberId ?? '',
      dateOfBirth: sub?.dateOfBirth ?? '',
      relationship: 'self',
      isDependent: false,
    })
    // Track origin so back/close returns to the right view
    setReturnToView(view)
    setEligibilityView('slideout')
  }

  /** Called by MedicalSlideout when a rerun produces a new result — updates state in-place. */
  function handleRerunResult(newResult: EligibilityResultWithMembers, provider: Provider, member: ConfirmedMember, collection?: EligibilityCheckDoc['collectionResult'] | null, checkId?: string | null, stediPayerId?: string | null, flowId?: string | null) {
    setResult(newResult)
    setSelectedProviderId(provider.id)
    setConfirmedMember(member)
    setCollectionResult(collection ?? null)
    setLegacyCollectionResult(null)
    setEligibilityCheckId(checkId ?? undefined)
    setSlideoutFlowId(flowId ?? null)
    setSlideoutStediId(stediPayerId ?? null)
    setSlideoutCheckMeta(null)
    setCorrectionOverride(null)
    setUserPick(null)
    // The rerun wrote a NEW check doc — the old check's recovery-loop state
    // doesn't apply to it.
    setSlideoutRecovery(null)
    setRecoveryReturn(null)
    invalidateChecks()
  }

  function handleStartCorrection() {
    const primary = primaryPaymentOption(collectionResult)
    posthog.capture('eligibility_feedback_initiated', {
      payer: result?.payer?.name,
      plan: result?.planName,
      originalAction: primary?.obligationType ?? 'unknown',
    })
    setCorrectionMode(true)
    setSourceRowHighlight(null)
    setView('eligibility')
    setEligibilityView('explorer')
  }

  async function handleCorrectionSubmit(selection: CorrectionSelection) {
    if (!collectionResult || !eligibilityCheckId) return

    const inPrimary = collectionResult.inNetwork?.paymentOptions[0] ?? null
    const oonPrimary = collectionResult.outOfNetwork?.paymentOptions[0] ?? null
    const primary = inPrimary ?? oonPrimary
    const originalAction = primary?.obligationType ?? 'unknown'
    const originalAmount = selection.network === 'out-of-network' && oonPrimary?.copay != null
      ? oonPrimary.copay
      : inPrimary?.copay ?? oonPrimary?.copay ?? null

    const override: CorrectionOverride = {
      selectedType: selection.type,
      selectedAmount: selection.amount,
      selectedPercent: selection.percent,
      selectedNetwork: selection.network,
      selectedStc: selection.stc,
      selectedStcLabel: selection.stcLabel,
      selectedDescription: selection.description,
      originalAction,
      originalAmount,
      originalAmountRange: null,
      explanation: selection.explanation,
      correctedBy: user.uid,
      correctedAt: new Date().toISOString(),
    }

    setCorrectionOverride(override)
    setCorrectionMode(false)
    setEligibilityView('slideout')

    posthog.capture('eligibility_feedback_submitted', {
      payer: result?.payer?.name,
      plan: result?.planName,
      originalAction,
      originalAmount,
      selectedType: selection.type,
      selectedAmount: selection.amount,
      selectedPercent: selection.percent,
    })

    try {
      await submitCorrection(orgId, eligibilityCheckId, override)
    } catch (err) {
      setCorrectionOverride(null)
      Sentry.captureException(err)
    }
  }

  function handleCorrectionDismiss() {
    posthog.capture('eligibility_feedback_dismissed', {
      payer: result?.payer?.name,
      plan: result?.planName,
    })
    setCorrectionMode(false)
    setEligibilityView('slideout')
  }

  async function handleRevertCorrection() {
    if (!eligibilityCheckId) return
    const prev = correctionOverride
    setCorrectionOverride(null)

    try {
      await revertCorrection(orgId, eligibilityCheckId)
    } catch (err) {
      setCorrectionOverride(prev)
      if (!demoEnabled) Sentry.captureException(err)
    }
  }

  async function handlePickOption(optionId: string) {
    if (!eligibilityCheckId) return
    const pick: UserPickOverride = {
      optionId,
      pickedBy: user.uid,
      pickedAt: new Date().toISOString(),
    }
    const prev = userPick
    setUserPick(pick)
    // Demo mode has no Firestore doc to write — keep the optimistic state only.
    if (demoEnabled) return
    try {
      await submitUserPick(orgId, eligibilityCheckId, pick)
    } catch (err) {
      setUserPick(prev)
      Sentry.captureException(err)
    }
  }

  async function handleClearPick() {
    if (!eligibilityCheckId) return
    const prev = userPick
    setUserPick(null)
    if (demoEnabled) return
    try {
      await clearUserPick(orgId, eligibilityCheckId)
    } catch (err) {
      setUserPick(prev)
      Sentry.captureException(err)
    }
  }

  // ---- Provider handlers ----

  /**
   * One default provider per clinic: a save that claims default at new
   * clinics must strip those clinics from every sibling's
   * `defaultForClinicIds`. Firestore has no cross-doc invariant, so the
   * write path owns it (same approach as the pre-#98 `isDefault` clearing).
   *
   * Only NEWLY claimed clinics are cleared — clinics the provider already
   * held stay untouched, so a manager's save never generates a sibling
   * write outside their own clinic (which the rules would deny).
   *
   * Array-shaped siblings get a DEFAULTS-ONLY update: echoing `clinicIds`
   * from page-load state would silently revert another user's concurrent
   * clinic assignment (lost update), and for managers a stale diff trips
   * providerAssignDiffMineOnly and fails the whole batch. The partial
   * update still passes providerWriteShape because rules evaluate the
   * post-merge doc, which keeps the stored `clinicIds`.
   *
   * Pre-migration LEGACY siblings (stored doc has no `clinicIds` —
   * hasClinicArrays false) are the exception: a defaults-only merge would
   * fail providerWriteShape, so they carry the full array shape, which
   * also opportunistically migrates them.
   */
  function buildSiblingDefaultClears(newlyClaimed: string[], excludeId?: string) {
    if (newlyClaimed.length === 0) return []
    return providers
      .filter(p => p.id !== excludeId && p.defaultForClinicIds.some(c => newlyClaimed.includes(c)))
      .map(p => ({
        id: p.id,
        fields: p.hasClinicArrays === false
          ? {
              clinicIds: p.clinicIds,
              defaultForClinicIds: p.defaultForClinicIds.filter(c => !newlyClaimed.includes(c)),
            }
          : {
              defaultForClinicIds: p.defaultForClinicIds.filter(c => !newlyClaimed.includes(c)),
            },
      }))
  }

  function applyProviderWrites(
    prev: Provider[],
    siblingUpdates: Array<{ id: string; fields: Partial<Omit<Provider, 'id'>> }>,
  ) {
    if (siblingUpdates.length === 0) return prev
    const byId = new Map(siblingUpdates.map(s => [s.id, s.fields]))
    return prev.map(p => {
      const fields = byId.get(p.id)
      if (!fields) return p
      // A write that carried clinicIds migrated a legacy doc to the array
      // shape — flip the read-time marker so later sibling clears in this
      // session use the defaults-only form.
      return { ...p, ...fields, ...('clinicIds' in fields ? { hasClinicArrays: true } : {}) }
    })
  }

  async function handleAddProvider(provider: Omit<Provider, 'id'>) {
    const siblingUpdates = buildSiblingDefaultClears(provider.defaultForClinicIds)
    const id = await ds.saveProviderWithDefaults(orgId, provider, siblingUpdates)
    setProviders(prev => [...applyProviderWrites(prev, siblingUpdates), { ...provider, id }])
    setSelectedProviderId(id)
  }

  /**
   * `fields` may be a partial update: managers editing a shared provider
   * send only the assignment arrays (the rules deny them identity keys,
   * and even an unchanged-value write of a key the doc lacks — e.g.
   * `taxId: ''` on a doc with no taxId — would count as an affected key).
   */
  async function handleUpdateProvider(
    id: string,
    fields: Partial<Omit<Provider, 'id'>> & Pick<Provider, 'clinicIds' | 'defaultForClinicIds'>,
  ) {
    const before = providersById.get(id)
    const newlyClaimed = fields.defaultForClinicIds.filter(
      c => !(before?.defaultForClinicIds ?? []).includes(c),
    )
    const siblingUpdates = buildSiblingDefaultClears(newlyClaimed, id)
    await ds.saveProviderWithDefaults(orgId, fields, siblingUpdates, id)
    // `fields` always carries clinicIds, so the stored doc is array-shaped
    // after this save — reflect that in the read-time marker.
    setProviders(prev => applyProviderWrites(prev, siblingUpdates)
      .map(p => p.id === id ? { ...p, ...fields, hasClinicArrays: true } : p))
    // A save that removes the selected provider from this clinic must reset
    // the eligibility form's selection, same as deletion — otherwise the
    // dropdown looks empty but still submits the removed provider's id,
    // which the backend rejects (hidden-id bug). Admins see the org-wide
    // list, so the provider stays selectable for them.
    if (selectedProviderId === id && !isAdmin && effectiveClinicId
        && !fields.clinicIds.includes(effectiveClinicId)) {
      const remaining = clinicProviders.filter(p => p.id !== id)
      setSelectedProviderId(remaining[0]?.id ?? '')
    }
  }

  async function handleDeleteProvider(id: string) {
    await ds.deleteProvider(orgId, id)
    setProviders(prev => prev.filter(p => p.id !== id))
    if (selectedProviderId === id) {
      // Reset from the clinic-scoped list, not the org directory — for a
      // manager, providers[0] can belong to another clinic, which the
      // eligibility form doesn't render but WOULD submit (hidden-id bug).
      const remaining = clinicProviders.filter(p => p.id !== id)
      setSelectedProviderId(remaining[0]?.id ?? '')
    }
  }

  // ---- Clinic handlers (admin only) ----

  async function handleAddClinic(fields: ClinicFields) {
    const id = await addClinic(orgId, fields, user.uid)
    const now = new Date().toISOString()
    setClinics(prev => [...prev, { id, orgId, ...fields, status: 'active', createdAt: now, createdBy: user.uid }])
  }

  async function handleUpdateClinic(id: string, fields: ClinicFields) {
    await updateClinic(orgId, id, fields)
    setClinics(prev => prev.map(c => c.id === id ? { ...c, ...fields } : c))
  }

  async function handleArchiveClinic(id: string) {
    await archiveClinic(orgId, id)
    setClinics(prev => prev.filter(c => c.id !== id))
  }

  // ---- Render ----

  /**
   * Boot gate: the body renders only after providers AND — for admins — the
   * clinics list have resolved, so `effectiveClinicId` is final before the
   * first paint of any view. This is what keeps the loading-null out of every
   * clinic-scoped surface (no assertion windows, no double PHI beacon, no
   * flash-refetch). Costs nothing: the cockpit already waited on
   * `clinicsLoaded` internally before showing rows.
   */
  const bootLoading = dataLoading || (isAdmin && !clinicsLoaded && !clinicsError)
  const bootReady = !bootLoading && !loadError && !clinicsError

  const showExplorer = eligibilityView === 'explorer' && result

  // SMS recovery UI is suppressed entirely when the feature is switched off, so
  // the slideouts' "SMS recovery" cross-link can't dead-end into the hidden
  // Recovery Loop page. Single choke point over the per-slideout recovery state.
  // A check reached from a Recovery Loop case IS in the loop for the link's
  // purposes, whatever its own doc says (a re-run in the chain is never
  // stamped) — the link exists to go back to that case.
  const recoveryUi = smsRecoveryEnabled
    ? (slideoutRecovery && recoveryReturn ? { ...slideoutRecovery, inLoop: true } : slideoutRecovery)
    : null

  return (
    <div className={`dashboard${impersonation ? ' dashboard--impersonating' : ''}`}>
      {impersonation && (
        <ImpersonationBanner
          info={impersonation}
          onReloadSession={onReloadSession ?? (() => window.location.reload())}
        />
      )}
      <div className="dashboard-shell">
        <NavSidebar
          view={view}
          onNavigate={handleNavClick}
          isManagerOrAdmin={isManagerOrAdmin}
          isAdmin={isAdmin}
          demoEnabled={demoEnabled}
          smsRecoveryEnabled={smsRecoveryEnabled}
          benefitsCaptureEnabled={benefitsCaptureEnabled}
          userEmail={user.email}
          userClinicName={userClinicName}
          workingClinics={isAdmin ? clinics : []}
          workingClinicId={effectiveClinicId}
          onWorkingClinicChange={handleWorkingClinicChange}
          onSignOut={onSignOut}
        />
        <div className="dashboard-float-status">
          <SyncHealthChip
            orgId={orgId}
            clinicId={clinicId}
            isAdmin={isAdmin}
            clinics={clinics}
          />
        </div>

      <div className={`dashboard-body ${showExplorer && view === 'eligibility' ? 'dashboard-body--explorer' : ''}`}>
        {bootLoading && (
          <main className="dashboard-main">
            <p className="dashboard-loading">Loading...</p>
          </main>
        )}
        {loadError && (
          <main className="dashboard-main">
            <div className="error-banner">
              <p className="error-banner__text">{loadError}</p>
            </div>
          </main>
        )}
        {!bootLoading && !loadError && clinicsError && (
          <main className="dashboard-main">
            <div className="error-banner">
              <p className="error-banner__text">{clinicsError}</p>
              <button className="btn btn--secondary" onClick={() => setClinicsRetry(n => n + 1)}>Retry</button>
            </div>
          </main>
        )}
        {bootReady && view === 'cockpit' && (!isAdmin && !effectiveClinicId ? (
          // A non-admin with no clinic assignment has no schedule to show —
          // say so instead of querying a clinic path with a null segment
          // (collection() throws synchronously on it).
          <main className="dashboard-main">
            <p className="clinics-view__empty">No clinic is assigned to your account. Ask your administrator to assign one.</p>
          </main>
        ) : (
          <DailyCockpit
            orgId={orgId}
            effectiveClinicId={effectiveClinicId}
            isAdmin={isAdmin}
            clinics={clinics}
            clinicsLoaded={clinicsLoaded}
            providers={providers}
            showClinicFilter={showClinicFilter}
            onRunCheck={(patient) => {
              // Run the check under the clinic the ROW belongs to — the check is
              // stamped with the working clinic and the row's first link below is
              // written by path, so a row from another clinic would be answered
              // under the wrong one and keep reading "Cannot check".
              const rowClinic = clinicSwitchForRow(patient.clinicId, effectiveClinicId, clinics)
              if (rowClinic.kind === 'unavailable') {
                // Switching to an archived clinic silently resolves elsewhere,
                // so proceeding would spend a check under a third clinic and
                // link nothing. Refuse instead — the server would reject it too
                // ("Clinic is not active."), only after a filled-in form.
                toast.error('This appointment belongs to an archived clinic, so a check cannot be run for it.')
                return
              }
              if (rowClinic.kind === 'switch') handleWorkingClinicChange(rowClinic.clinicId)
              setPrefillFormData({
                firstName: patient.firstName,
                lastName: patient.lastName,
                dateOfBirth: patient.dob ?? '',
                memberId: patient.policyNumber ?? '',
                payerName: stripPayerIdSuffix(patient.payer ?? ''),
                appointmentDate: patient.appointmentDate ? firestoreDateToIso(patient.appointmentDate) : '',
                // Re-check of this row (stale / not-checked) continues the
                // row's flow so the Cockpit follows the fresh result; the
                // linked check lets the server inherit the chain's patient
                // contact + SMS candidacy (never sourced from the form).
                flowId: patient.flowId ?? undefined,
                sourceCheckId: patient.eligibilityCheckId ?? undefined,
                // A row that has never had a check has no flow to continue and
                // nothing for the server's relink to find, so it needs the row
                // itself. Without this the check runs, succeeds, and the row
                // still reads "Cannot check" — and the next person to open the
                // day repeats the work. Uninsured rows are the whole cohort.
                appointmentId: patient.eligibilityCheckId ? undefined : patient.id,
              })
              setFormResetKey(k => k + 1)
              setView('eligibility')
              setEligibilityView('form')
            }}
            onViewCheck={(patient, check) => {
              // Stedi check with 271 result → medical slideout. Prefer the
              // check's original provider; fall back to any clinic provider;
              // last-resort synthesize a stand-in (only reached when the
              // clinic has zero providers at all) so the result still renders.
              if (check.result) {
                const provider: Provider = providers.find(p => p.id === check.providerId) ?? providers[0] ?? {
                  id: check.providerId || 'unknown',
                  clinicId: check.clinicId ?? '',
                  firstName: patient.providerName?.trim() || 'Background check',
                  lastName: '',
                  npi: '',
                  phone: '',
                  email: '',
                }
                handleBatchViewResult(check.result, provider, check.patientId, check.requestSubscriber, check.collectionResult, check.id, check.correction, patient.payer ?? undefined, check.checkedAt, check.clinicId, check.userPick, check.legacyCollectionResult, check.stediPayerId, check)
                return
              }
              // Vision check with benefits → vendor-specific slideout. The header
              // is built from the check doc (same as Recent Checks); the Cockpit
              // row supplies the appointment date + raw payer as fallbacks.
              // openVisionCheck logs the PHI view (#185).
              if (openVisionCheck(check, {
                returnTo: 'cockpit',
                fallbackAppointmentDate: patient.appointmentDate ? firestoreDateToIso(patient.appointmentDate) : undefined,
                fallbackRawPayer: patient.payer ?? undefined,
              })) {
                return
              }
              // Failed check — no 271 and no benefits. Show the failure slideout
              // with the same shared header as every other slideout (built from
              // the check; the Cockpit row supplies the appointment date + raw
              // payer as fallbacks — the raw payer drives the AI-match badge).
              const apptIso = patient.appointmentDate ? firestoreDateToIso(patient.appointmentDate) : ''
              setFailedCheckCtx({
                check,
                summary: buildVisionSummary(check, {
                  fallbackAppointmentDate: apptIso || undefined,
                  fallbackRawPayer: patient.payer ?? undefined,
                }),
                prefill: {
                  firstName: patient.firstName,
                  lastName: patient.lastName,
                  dateOfBirth: patient.dob ?? '',
                  memberId: patient.policyNumber ?? '',
                  payerName: stripPayerIdSuffix(patient.payer ?? ''),
                  appointmentDate: apptIso,
                  flowId: check.flowId ?? undefined,
                  sourceCheckId: check.id,
                },
              })
              setReturnToView('cockpit')
              setEligibilityView('failureSlideout')
            }}
          />
        ))}
        {bootReady && view === 'doctors' && isManagerOrAdmin && (
          effectiveClinicId ? (
            <DoctorsList
              providers={providers}
              clinics={clinics}
              clinicId={effectiveClinicId}
              isAdmin={isAdmin}
              onAddProvider={handleAddProvider}
              onUpdateProvider={handleUpdateProvider}
              onDeleteProvider={handleDeleteProvider}
            />
          ) : (
            <div className="doctors-view">
              <p className="clinics-view__empty">Create a clinic first before adding providers.</p>
            </div>
          )
        )}
        {/* Analytics hits the live getRoiAnalytics callable — no demo dataset
            behind it yet, so the nav item is hidden in demo mode (see #118). */}
        {view === 'analytics' && isManagerOrAdmin && !demoEnabled && (
          <RoiAnalytics />
        )}
        {/* Benefits Capture reads the builder's clinic-day docs directly under
            the rules — every role, the whole practice — behind the staff switch. */}
        {view === 'benefitsCapture' && benefitsCaptureEnabled && (
          <BenefitsCapture orgId={orgId} frontDeskGoal={frontDeskGoal} />
        )}
        {view === 'clinics' && isAdmin && (
          <ClinicsList
            clinics={clinics}
            onAddClinic={handleAddClinic}
            onUpdateClinic={handleUpdateClinic}
            onArchiveClinic={handleArchiveClinic}
          />
        )}
        {view === 'users' && isAdmin && (
          // Single flex-child of .dashboard-body (a flex row). DemoControls and
          // UsersList must stack in a column here, otherwise they split into
          // two columns and the bare demo-controls div stretches to fill the
          // left half of the page.
          <div className="demo-view-wrap">
            <DemoControls orgId={orgId} onReset={refreshUsers} />
            <UsersList
              users={orgUsers}
              invites={orgInvites}
              clinics={clinics.length > 0 ? clinics : []}
              loading={usersLoading}
              error={usersError}
              currentUid={user.uid}
              onRefresh={refreshUsers}
            />
          </div>
        )}
        {bootReady && view === 'rpaJobs' && isManagerOrAdmin && (
          <RpaJobDashboard
            orgId={orgId}
            clinicId={clinicId}
            isAdmin={isAdmin}
            role={role}
            clinics={clinics}
          />
        )}
        {view === 'integrations' && isAdmin && (
          <IntegrationsList
            clinics={clinics}
            orgId={orgId}
            isAdmin={isAdmin}
            canWrite={canWriteTenant}
            onIntegrationSaved={() => setClinicsRetry(n => n + 1)}
          />
        )}
        {bootReady && view === 'recentChecks' && !checksLoading && (
          <RecentChecksTable
            filteredChecks={filteredChecks}
            loadedCount={recentChecks.length}
            providers={providers}
            clinics={clinics}
            isAdmin={isAdmin}
            showClinicFilter={showClinicFilter}
            onViewDetails={handleBatchViewResult}
            onViewVspCheck={(check) => {
              // openVisionCheck logs the PHI view (#185).
              openVisionCheck(check, { returnTo: 'recentChecks' })
            }}
            onViewFailedCheck={(check) => {
              // Prefill data comes from whatever the check doc carries; the
              // header uses the same buildVisionSummary as every slideout.
              const firstName = check.requestSubscriber?.firstName ?? check.identity?.firstName ?? ''
              const lastName = check.requestSubscriber?.lastName ?? check.identity?.lastName ?? ''
              const dob = check.requestSubscriber?.dateOfBirth ?? check.identity?.dob ?? ''
              const memberId = check.requestSubscriber?.memberId ?? check.identity?.memberId ?? check.identity?.policyNumber ?? ''
              const payer = check.payerName ?? check.result?.payer?.name ?? ''
              setFailedCheckCtx({
                check,
                summary: buildVisionSummary(check),
                prefill: {
                  firstName,
                  lastName,
                  dateOfBirth: dob,
                  memberId,
                  payerName: stripPayerIdSuffix(payer),
                  appointmentDate: check.appointmentDate ?? '',
                  flowId: check.flowId ?? undefined,
                  sourceCheckId: check.id,
                },
              })
              setReturnToView('recentChecks')
              setEligibilityView('failureSlideout')
            }}
            error={checksError}
            hasMore={checksHasMore}
            loadingMore={checksLoadingMore}
            onLoadMore={handleLoadMoreChecks}
            checksSort={checksSort}
            onSortChange={handleSortChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            clinicFilter={clinicFilter}
            onClinicFilterChange={handleClinicFilterChange}
            windowOption={windowOption}
            onWindowOptionChange={handleWindowOptionChange}
          />
        )}
        {bootReady && view === 'recoveryLoop' && smsRecoveryEnabled && (
          <RecoveryLoopList
            orgId={orgId}
            clinicId={effectiveClinicId}
            isAdmin={isAdmin}
            isStaff={isStaff}
            canWrite={canWriteTenant}
            clinics={clinics}
            focusCheckId={recoveryFocusCheckId}
            onFocusConsumed={() => setRecoveryFocusCheckId(null)}
            onOpenCheck={handleOpenCheckFromRecovery}
          />
        )}
        {bootReady && view === 'eligibility' && (!effectiveClinicId ? (
          // Zero active clinics (e.g. the org's only clinic was archived):
          // an empty state, per the effectiveClinicId contract — never let the
          // form submit a null clinicId for the server to bounce.
          <div className="doctors-view">
            <p className="clinics-view__empty">Create a clinic first before running eligibility checks.</p>
          </div>
        ) : (
          <>
            {eligibilityView === 'form' && (
              <EligibilityForm
                key={formResetKey}
                providers={clinicProviders}
                selectedProviderId={selectedProviderId}
                onProviderChange={handleProviderChange}
                checking={checking}
                error={error}
                onRetry={error?.retryable ? handleRetry : undefined}
                onCheck={handleCheck}
                onUploadCsv={() => setEligibilityView('csvUpload')}
                initialData={prefillFormData}
                clinicId={effectiveClinicId}
                clinics={clinics}
                isManagerOrAdmin={isManagerOrAdmin}
                onGoToDoctors={() => setView('doctors')}
                onRefreshProviders={() => loadData({ background: true })}
              />
            )}
            {eligibilityView === 'csvUpload' && (
              <Suspense fallback={<div className="dashboard-main" />}>
                <CsvUploadView
                  orgId={orgId}
                  clinicId={effectiveClinicId}
                  clinics={clinics}
                  isAdmin={isAdmin}
                  providers={clinicProviders}
                  onBack={() => setEligibilityView('form')}
                  onComplete={() => { invalidateChecks(); setView('cockpit') }}
                />
              </Suspense>
            )}
            {eligibilityView === 'slideout' && result && selectedProvider && confirmedMember && (
              <EligibilityForm
                key={formResetKey}
                providers={clinicProviders}
                selectedProviderId={selectedProviderId}
                onProviderChange={handleProviderChange}
                checking={checking}
                error={error}
                onRetry={error?.retryable ? handleRetry : undefined}
                onCheck={handleCheck}
                clinicId={effectiveClinicId}
                clinics={clinics}
                isManagerOrAdmin={isManagerOrAdmin}
                onGoToDoctors={() => setView('doctors')}
                onRefreshProviders={() => loadData({ background: true })}
              />
            )}
            {eligibilityView === 'memberSelect' && result && (
              <MemberSelector
                members={result.members}
                patientContext={lastPatientContext}
                planName={result.planName}
                payerName={stripPayerIdSuffix(result.payer.name)}
                onSelect={handleMemberSelected}
                onBack={handleBack}
              />
            )}
            {eligibilityView === 'explorer' && result && selectedProvider && confirmedMember && (
              <Sentry.ErrorBoundary fallback={
                <div className="error-banner">
                  <p className="error-banner__text">Something went wrong rendering benefits. The error has been reported.</p>
                  <button className="btn btn--secondary" onClick={handleBack}>Back to eligibility check</button>
                </div>
              }>
                <BenefitsExplorer
                  result={result}
                  confirmedMember={confirmedMember}
                  selectedProvider={selectedProvider}
                  onBack={handleExplorerBack}
                  onEditPatient={result.status !== 'active' ? handleBackToSlideout : undefined}
                  highlight={sourceRowHighlight}
                  onHighlightConsumed={() => setSourceRowHighlight(null)}
                  correctionMode={correctionMode}
                  onCorrectionSubmit={handleCorrectionSubmit}
                  onCorrectionDismiss={handleCorrectionDismiss}
                />
              </Sentry.ErrorBoundary>
            )}
          </>
        ))}
      </div>
      </div>

      {eligibilityView === 'slideout' && result && selectedProvider && confirmedMember && (
        <MedicalSlideout
          result={result}
          confirmedMember={confirmedMember}
          selectedProvider={selectedProvider}
          onClose={handleSlideoutClose}
          onViewFullBenefits={handleViewFullBenefits}
          onViewSourceRows={handleViewSourceRows}
          orgId={orgId}
          // The check's own clinic when we are showing a stored one — see
          // `slideoutClinicId`. A fresh run has none and did run under the
          // working clinic.
          clinicId={slideoutClinicId ?? effectiveClinicId}
          providers={providers}
          patientId={slideoutPatientId}
          onRerunResult={handleRerunResult}
          collectionResult={collectionResult}
          legacyCollectionResult={legacyCollectionResult}
          checkId={eligibilityCheckId}
          flowId={slideoutFlowId}
          storedStediId={slideoutStediId}
          checkedAt={slideoutCheckMeta?.checkedAt}
          timezone={slideoutCheckMeta?.timezone}
          correctionOverride={correctionOverride}
          onStartCorrection={handleStartCorrection}
          onRevertCorrection={handleRevertCorrection}
          originalPayerName={originalPayerName}
          userPick={userPick}
          onPickOption={handlePickOption}
          onClearPick={handleClearPick}
          inRecoveryLoop={recoveryUi?.inLoop}
          smsRecoveryEligible={recoveryUi?.smsEligible}
          onViewRecoveryConversation={smsRecoveryEnabled && eligibilityCheckId
            ? () => handleViewRecoveryConversation(eligibilityCheckId)
            : undefined}
        />
      )}

      {eligibilityView === 'vspSlideout' && vspBenefits && visionSummary && (
        <VspSlideout
          orgId={orgId}
          check={visionCheckDoc}
          onCorrected={() => { invalidateChecks(); handleSlideoutClose() }}
          onCorrectionFired={invalidateChecks}
          canWrite={canWriteTenant}
          onDirtyChange={d => { setDetailDirty(d); if (!d) setDetailWarned(false) }}
          warnedByParent={detailWarned}
          vspBenefits={vspBenefits}
          summary={visionSummary}
          onClose={handleSlideoutClose}
          inRecoveryLoop={recoveryUi?.inLoop}
          smsRecoveryEligible={recoveryUi?.smsEligible}
          onViewRecoveryConversation={recoveryUi
            ? () => handleViewRecoveryConversation(recoveryUi.checkId)
            : undefined}
        />
      )}

      {eligibilityView === 'eyemedSlideout' && eyemedBenefits && visionSummary && (
        <EyemedSlideout
          orgId={orgId}
          check={visionCheckDoc}
          onCorrected={() => { invalidateChecks(); handleSlideoutClose() }}
          onCorrectionFired={invalidateChecks}
          canWrite={canWriteTenant}
          onDirtyChange={d => { setDetailDirty(d); if (!d) setDetailWarned(false) }}
          warnedByParent={detailWarned}
          benefits={eyemedBenefits}
          summary={visionSummary}
          onClose={handleSlideoutClose}
          inRecoveryLoop={recoveryUi?.inLoop}
          smsRecoveryEligible={recoveryUi?.smsEligible}
          onViewRecoveryConversation={recoveryUi
            ? () => handleViewRecoveryConversation(recoveryUi.checkId)
            : undefined}
        />
      )}

      {eligibilityView === 'versantSlideout' && versantBenefits && visionSummary && (
        <VersantSlideout
          orgId={orgId}
          check={visionCheckDoc}
          onCorrected={() => { invalidateChecks(); handleSlideoutClose() }}
          onCorrectionFired={invalidateChecks}
          canWrite={canWriteTenant}
          onDirtyChange={d => { setDetailDirty(d); if (!d) setDetailWarned(false) }}
          warnedByParent={detailWarned}
          benefits={versantBenefits}
          summary={visionSummary}
          onClose={handleSlideoutClose}
          inRecoveryLoop={recoveryUi?.inLoop}
          smsRecoveryEligible={recoveryUi?.smsEligible}
          onViewRecoveryConversation={recoveryUi
            ? () => handleViewRecoveryConversation(recoveryUi.checkId)
            : undefined}
        />
      )}

      {eligibilityView === 'failureSlideout' && failedCheckCtx && (
        <CheckFailureSlideout
          check={failedCheckCtx.check}
          summary={failedCheckCtx.summary}
          onClose={handleSlideoutClose}
          orgId={orgId}
          onDirtyChange={d => { setDetailDirty(d); if (!d) setDetailWarned(false) }}
          warnedByParent={detailWarned}
          onCorrectionFired={invalidateChecks}
          canWrite={canWriteTenant}
          onCorrected={() => {
            // The corrected check joins the same flow, so the Cockpit row and
            // Recent Checks follow it on their own subscriptions; closing the
            // panel is enough to land the operator back on the updated row.
            invalidateChecks()
            setFailedCheckCtx(null)
            // This panel is closing, so the case it was reached from no longer
            // applies — left set, the NEXT check opened claimed the patient was
            // texted and linked to that earlier case.
            setRecoveryReturn(null)
          }}
          onViewRecoveryConversation={smsRecoveryEnabled
            ? () => handleViewRecoveryConversation(failedCheckCtx.check.id)
            : undefined}
          inRecoveryLoop={recoveryReturn ? true : undefined}
          onEditAndRetry={() => {
            setPrefillFormData(failedCheckCtx.prefill)
            setFormResetKey(k => k + 1)
            setFailedCheckCtx(null)
            // Same as onCorrected: the panel is closing, and the form is the
            // one sub-view a nav click does not reset, so this is the only
            // place the pointer can be cleared on this path.
            setRecoveryReturn(null)
            setReturnToView(null)
            setView('eligibility')
            setEligibilityView('form')
          }}
        />
      )}

      {(checking || checksLoading) && (
        <div className="loading-modal-overlay">
          <div className="loading-modal">
            <div className="loading-modal__spinner" />
            <p className="loading-modal__text">
              {checksLoading ? 'Loading recent checks...' : 'Checking eligibility...'}
            </p>
          </div>
        </div>
      )}

      {jsonExportEnabled && result && (
        <>
          <button
            className="dev-raw-json-fab"
            onClick={() => setShowRawJson(true)}
            title="Show / download JSON"
          >
            {'{ }'}
          </button>
          <RawJsonModal
            open={showRawJson}
            onClose={() => setShowRawJson(false)}
            data={deidentifyEligibilityExport({
              result,
              ...(collectionResult ? { collectionResult } : {}),
              ...(legacyCollectionResult ? { legacyCollectionResult } : {}),
            })}
            filenameBase={`eligibility-${result.payer?.name || 'check'}-${eligibilityCheckId ?? 'manual'}`}
          />
        </>
      )}
      <Toaster position="bottom-right" richColors closeButton expand />
    </div>
  )
}
